import { boxAt, boxEnvironment, lightContext, PERCEPTION_LIGHT, type PerceptionPosition, type PerceptionShape, type PerceptionContextSeed } from '../../encounter-map/ft3d/perception';
import { _ftCamField } from '../../shared/campaign-field';
import { encounterField, layerOf, layerValue } from '../../shared/array-types';
import { boxIsRoofed, shapeLightFixtures, validateShape } from '../../../../ft3d/engine/ft3d-shape-core.js';

type Row = Record<string, unknown>;
/** Raw campaign/catalog facts use the page's existing shell-aware readers. */
export function clientPerceptionContext(encounter: Row, shape: PerceptionShape, campaign: Row, fixture: Row): PerceptionContextSeed {
  const light = lightContext({ ...encounter, campaign_light: _ftCamField(campaign, 'campaign_light') }, shape);
  const radius = Number(layerValue(layerOf(fixture, 'bright_light_dim_light')) ?? 0);
  if (!Number.isFinite(radius)) throw new Error('geometry:fixture-light-unavailable');
  return { schema: 'ft.ft3d.perception-context.v1', override: light.override, effective: light.effective,
    underground: light.underground, fixtureLightRadius: Math.abs(radius), queryOnly: true, writes: 0 };
}

type PerceptionWindow = {
  ft3dReadPerceptionContext?: () => { encounterId: string; context: PerceptionContextSeed | null; refreshPending?: boolean };
  ft3dGeometryShape?: () => PerceptionShape | null;
  FateTide?: { campaignCache?: Map<string, unknown>; currentCampaignArr?: unknown };
};

function sceneHosts(): PerceptionWindow[] {
  if (typeof window === 'undefined') return [];
  const hosts: PerceptionWindow[] = [window as unknown as PerceptionWindow];
  for (const frame of Array.from(window.document?.querySelectorAll?.<HTMLIFrameElement>('[data-ft3d-encounter-frame]') ?? [])) {
    if (frame.contentWindow) hosts.push(frame.contentWindow as unknown as PerceptionWindow);
  }
  return hosts;
}

/** The scene already projects doors, breaches, curtains and roofs. Read its current, encounter-bound geometry without IO. */
export function pageSceneShape(encounter: Row): PerceptionShape | null {
  const id = String(encounterField(encounter, 'encounter_id') ?? '').toUpperCase();
  if (!id) return null;
  for (const host of sceneHosts()) {
    try {
      if (host.ft3dReadPerceptionContext?.().encounterId.toUpperCase() !== id) continue;
      const shape = host.ft3dGeometryShape?.();
      if (shape) { validateShape(shape); return shape; }
    } catch { /* An unavailable scene leaves the authored shape's overlays unknown. */ }
  }
  return null;
}

/** Casts, attacks and their projected reactions use the scene's current light context. No bundle-local state. */
export function pagePerceptionContext(encounter: Row, shape: PerceptionShape,
  sightPoints: readonly PerceptionPosition[] = []): PerceptionContextSeed | null {
  const id = String(encounterField(encounter, 'encounter_id') ?? '').toUpperCase();
  if (!id) return null;
  if (typeof window !== 'undefined') {
    for (const host of sceneHosts()) {
      try {
        const held = host.ft3dReadPerceptionContext?.();
        if (held?.encounterId.toUpperCase() === id) {
          if (held.refreshPending) return null;
          if (held.context) return held.context;
        }
      } catch { /* Scene is not ready; try the held row facts below. */ }
    }
  }
  // Automatic light is an omitted/null own-row column, not an unknown scene. Use the campaign's shell-aware reader.
  const parentId = String(encounterField(encounter, 'encounter_master_parent_id') ?? '').toUpperCase();
  const ft = typeof window === 'undefined' ? undefined : (window as unknown as PerceptionWindow).FateTide;
  const campaign = [ft?.campaignCache?.get(parentId), ft?.currentCampaignArr]
    .find((row): row is Row => !!row && typeof row === 'object'
      && _ftCamField(row as Row, 'campaign_id')?.toUpperCase() === parentId);
  const campaignLight = encounterField(encounter, 'campaign_light') ?? _ftCamField(campaign, 'campaign_light');
  const knownLight = (value: unknown): boolean => (Object.values(PERCEPTION_LIGHT) as unknown[]).includes(value);
  // A sealed interior proves darkness only for sight endpoints held inside its boxes, never Scenery ground or roof flight.
  const sealed = sightPoints.length >= 2 && sightPoints.every(point => boxAt(shape, point) !== null)
    && shape.boxes.length > 0 && !(shape.openings?.length)
    && shape.boxes.every(box => boxIsRoofed(box) && boxEnvironment(box, shape) === 'interior');
  if (!knownLight(encounterField(encounter, 'encounter_light')) && !knownLight(campaignLight) && !sealed) return null;
  const context = clientPerceptionContext(encounter, shape, { campaign_light: campaignLight }, {});
  // Unknown sconces cannot change a Bright / Dim override, but in any darkness — magical included (lightAt / calculateLightAt dim light only
  // inside dark SOURCES; an ambient magical-darkness base is beaten by a bright source) — their unknown radius makes the verdict unknown.
  if (shapeLightFixtures(shape).length && context.override !== PERCEPTION_LIGHT.bright
    && context.override !== PERCEPTION_LIGHT.dim) return null;
  return context;
}
